import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Info, Database, Layers2 } from "lucide-react";
import { ContentApi } from "../../../shared/api/contentApi";
import { notify } from "shared/utils/notify";
import formatDate from "../../../shared/helpers/formatDate";
// Импорты ваших UI компонентов (добавьте актуальные пути)
import DetailPageHero from "../../../components/ui/DetailPageHero";
import DetailTabs, { DetailTabsContent } from "../../../components/ui/DetailTabs";
import DetailPageSidebar from "../../../components/ui/DetailPageSidebar";
import CreateVersionDialog from "../components/CreateVersionDialog";
import ConfirmDialog from "../../../components/ui/ConfirmDialog";
import VersionEditDialog from "../components/EditVersionDialog";
import { Modal, Flex, Button, Card, Splitter, Switch, Typography } from "antd";
import { DETAIL_CARD_CLASS } from "../../../shared/constants/detailLayout";

export default function VersionDetails() {
    const { projectId, versionId } = useParams(); // ID текущей версии
    const navigate = useNavigate();

    const [version, setVersion] = useState(null);
    const [project, setProject] = useState(null); // Родительский проект
    const [projectVersions, setProjectVersions] = useState([]); // Другие версии этого проекта

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [createVersionOpen, setCreateVersionOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editVersionOpen, setEditVersionOpen] = useState(false);
    const [selectedVersionId, setSelectedVersionId] = useState(null); // Для отображения выбранной версии в сайдбаре

    const loadDetails = async (signal) => {
        if (!versionId) {
            setVersion(null);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError("");
        try {
            // 1. Получаем данные конкретной версии
            const versionData = await ContentApi.getVersionById(projectId, versionId, { signal });
            setVersion(versionData || null);

            if (projectId) {
                const [projectData, versionsData] = await Promise.all([
                    ContentApi.getProjectById(projectId, { signal }),
                    ContentApi.getVersions({ projectId: projectId, page: 1, pageSize: 1000 }, { signal }),
                ]);
                setProject(projectData || null);
                setProjectVersions(Array.isArray(versionsData?.items) ? versionsData.items : []);
            }
        } catch (e) {
            if (!signal?.aborted) {
                setError(e?.message || "Ошибка загрузки данных версии");
            }
        } finally {
            if (!signal?.aborted) setLoading(false);
        }
    };

    // Сортируем все версии проекта по дате обновления
    const sortedVersions = useMemo(() => {
        return [...projectVersions].sort((left, right) => {
            const leftTime = Date.parse(left?.updatedAt || 0) || 0;
            const rightTime = Date.parse(right?.updatedAt || 0) || 0;
            return rightTime - leftTime;
        });
    }, [projectVersions]);

    const selectedVersion = useMemo(() => {
        return sortedVersions.find((version) => String(version.id) === String(selectedVersionId))
            || sortedVersions[0]
            || null;
    }, [selectedVersionId, sortedVersions]);

    useEffect(() => {
        const ac = new AbortController();
        loadDetails(ac.signal);

        return () => ac.abort();
    }, [versionId]);

    const handleVersionDelete = async () => {
        try {
            await ContentApi.deleteVersion(projectId, versionId);
            notify.success("Версия успешно удалена");
            // После удаления версии возвращаем пользователя на страницу родительского проекта
            if (version?.projectId) {
                navigate(`/projects/${version.projectId}`);
            } else {
                navigate("/projects/list");
            }
        } catch (err) {
            notify.error(`Ошибка удаления версии: ${err.message}`);
        } finally {
            setDeleteDialogOpen(false);
        }
    };

    const tabs = [
        { value: "overview", label: "Обзор", icon: <Info size={16} /> },
        { value: "usage", label: "Использование", icon: <Database size={16} /> },
        { value: "meta", label: "Метаданные", icon: <Info size={16} /> },
    ];

    if (loading) {
        return (
            <section className="p-4 md:p-6">
                <div className="p-6 text-sm text-zinc-500">Загрузка данных версии…</div>
            </section>
        );
    }

    if (error) {
        return (
            <section className="p-4 md:p-6">
                <div className="rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                    {error}
                </div>
            </section>
        );
    }

    if (!version) {
        return (
            <section className="p-4 md:p-6">
                <div className="p-6 text-sm text-zinc-500">Версия не найдена</div>
            </section>
        );
    }

    const CustomSplitter = ({ style, ...restProps }) => (
        <Splitter style={{ boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)', ...style }} {...restProps}>
            <Splitter.Panel collapsible min="20%">
                <Desc text="First" />
            </Splitter.Panel>
            <Splitter.Panel collapsible>
                <Desc text="Second" />
            </Splitter.Panel>
        </Splitter>
    );
    const Desc = props => (
        <Flex justify="center" align="center" style={{ height: '100%' }}>
            <Typography.Title type="secondary" level={5} style={{ whiteSpace: 'nowrap' }}>
                {props.text}
            </Typography.Title>
        </Flex>
    );

    return (
        <section className="p-4 md:p-6 space-y-6">
            <div className="mx-auto w-full max-w-6xl">
                <Flex vertical gap="large" className="mt-2 w-full">
                    <div className="w-full">
                        <DetailTabs defaultValue="overview" tabs={tabs} onDelete={() => setDeleteDialogOpen(true)}>
                            {/* Overview Tab */}
                            <DetailTabsContent value="overview" className="space-y-4">
                                <DetailPageHero
                                    title={version.name || `Версия #${version.id}`}
                                    subtitle={version.systemName}
                                    breadcrumbs={[
                                        { label: "Проекты", to: "/projects/list" },
                                        { label: project?.name || "Проект", to: project ? `/projects/${project.id}` : undefined },
                                        { label: version.name || "Детали версии" },
                                    ]}
                                    metadata={[
                                        { label: "ID Версии", value: `#${version.id}` },
                                        { label: "Статус", value: version.status || "UNKNOWN" },
                                    ]}
                                />

                                <Card title="Описание" className={DETAIL_CARD_CLASS}>
                                    {version.description ? (
                                        <p className="text-sm">{version.description}</p>
                                    ) : (
                                        <p className="text-sm text-zinc-500">Нет описания у данной версии</p>
                                    )}
                                </Card>

                                <Card title="Дополнительно" className={DETAIL_CARD_CLASS}>
                                    <div>
                                        <dt className="eyebrow">Комментарий</dt>
                                        <p className="text-sm text-zinc-500">
                                            {version.comment || '-'}
                                        </p>
                                    </div>
                                    <div>
                                        <dt className="eyebrow">Номер версии</dt>
                                        <dd className="mt-0.5">{version.number ?? "-"}</dd>
                                    </div>
                                    <div className="mt-4">
                                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                            Создана: {version?.createdAt ? formatDate(version.createdAt) : "-"}
                                            <div>
                                                Обновлено: {version?.updatedAt ? formatDate(version.updatedAt) : version?.createdAt ? formatDate(version.createdAt) : "-"}
                                            </div>
                                        </div>
                                        <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                            <div>Создана кем: {version?.createdBy ?? "-"}</div>
                                            <div>
                                                Обновлено кем: {version?.updatedBy ?? "-"}
                                            </div>
                                        </div>
                                    </div>
                                </Card>
                            </DetailTabsContent>
                            <DetailTabsContent value="usage" className="space-y-4">
                                <Splitter style={{ boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)' }} >
                                    <Splitter.Panel collapsible min="20%">
                                        <Card title={`Все версии проекта (${project?.name})`} className={DETAIL_CARD_CLASS}>
                                            <Flex align="center" justify="space-between" gap="small" wrap className="mb-3">
                                                <h2 className="text-xm">Можете создать форк версии</h2>
                                                <Button type="primary" onClick={() => setCreateVersionOpen(true)}>
                                                    Создать форк версии
                                                </Button>
                                            </Flex>

                                            {sortedVersions.length === 0 ? (
                                                <p className="text-sm text-zinc-500">У проекта пока нет других версий.</p>
                                            ) : (
                                                <div className="space-y-2">
                                                    {sortedVersions.map((v) => {
                                                        const isCurrent = String(v.id) === String(versionId);
                                                        const isSelected = String(selectedVersion?.id) === String(v.id);
                                                        return (
                                                            <button
                                                                type="button"
                                                                key={v.id}
                                                                aria-pressed={isSelected}
                                                                onClick={() => setSelectedVersionId(String(v.id))}
                                                                className={`rounded-lg border p-3 transition-colors ${isCurrent
                                                                    ? "border-blue-500 bg-blue-50/30 dark:bg-blue-950/20"
                                                                    : "border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
                                                                    }`}
                                                            >
                                                                <Flex flex align="flex-start" wrap="wrap" justify="space-between" gap="medium">
                                                                    <Flex flex align="flex-start" gap="small" wrap="wrap" vertical>
                                                                        <Link
                                                                            to={`/projects/${projectId}/versions/${v.id}`}
                                                                            className={`text-sm font-medium hover:underline ${isCurrent ? 'text-blue-600 dark:text-blue-400' : ''}`}
                                                                        >
                                                                            {v.name || v.systemName || `#${v.id}`}
                                                                        </Link>
                                                                        <div className="text-xs text-zinc-500">ID: {v.id} {isCurrent && "(Текущая)"}</div>
                                                                    </Flex>
                                                                    <span className="chip chip-muted">{v.status || "UNKNOWN"}</span>
                                                                </Flex>
                                                                <div className="justify-items-start mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-zinc-500">
                                                                    <div>Номер: {v.number ?? "-"}</div>
                                                                    <div>Обновлено: {formatDate(v.updatedAt)}</div>
                                                                </div>
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </Card>

                                    </Splitter.Panel>
                                    <Splitter.Panel collapsible>
                                        {selectedVersion ? (
                                            <Card
                                                title={selectedVersion.name || selectedVersion.systemName || `Версия #${selectedVersion.id}`}
                                                className="h-full w-full"
                                            >
                                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                    <div>
                                                        <dt className="eyebrow">System Name</dt>
                                                        <dd className="mt-0.5 break-words">{selectedVersion.systemName || "-"}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="eyebrow">Статус</dt>
                                                        <dd className="mt-0.5">
                                                            <span className="chip chip-muted">{selectedVersion.status || "UNKNOWN"}</span>
                                                        </dd>
                                                    </div>
                                                    <div>
                                                        <dt className="eyebrow">ID</dt>
                                                        <dd className="mt-0.5 break-all">{selectedVersion.id}</dd>
                                                    </div>
                                                    <div>
                                                        <dt className="eyebrow">Номер версии</dt>
                                                        <dd className="mt-0.5">{selectedVersion.number ?? "-"}</dd>
                                                    </div>
                                                </div>

                                                <div className="mt-5">
                                                    <dt className="eyebrow">Описание</dt>
                                                    <dd className="mt-1 whitespace-pre-wrap text-sm">
                                                        {selectedVersion.description || "Нет описания у данной версии"}
                                                    </dd>
                                                </div>

                                                <div className="mt-5">
                                                    <dt className="eyebrow">Комментарий</dt>
                                                    <dd className="mt-1 whitespace-pre-wrap text-sm text-zinc-500">
                                                        {selectedVersion.comment || "-"}
                                                    </dd>
                                                </div>

                                                <div className="mt-5 grid grid-cols-1 gap-2 text-xs text-zinc-500 sm:grid-cols-2">
                                                    <div>Создана: {selectedVersion.createdAt ? formatDate(selectedVersion.createdAt) : "-"}</div>
                                                    <div>
                                                        Обновлено: {selectedVersion.updatedAt
                                                            ? formatDate(selectedVersion.updatedAt)
                                                            : selectedVersion.createdAt
                                                                ? formatDate(selectedVersion.createdAt)
                                                                : "-"}
                                                    </div>
                                                    <div>Создана кем: {selectedVersion.createdBy ?? "-"}</div>
                                                    <div>Обновлено кем: {selectedVersion.updatedBy ?? "-"}</div>
                                                </div>
                                                <Button
                                                    onClick={() => navigate(`/projects/${projectId}/versions/${selectedVersion.id}`)}
                                                    className="mt-4"
                                                    type="primary"
                                                    block
                                                    icon={<Layers2 size={16} />}
                                                >
                                                    Перейти к версии
                                                </Button>
                                            </Card>
                                        ) : (
                                            <Flex justify="center" align="center" style={{ height: "100%" }}>
                                                <p className="text-sm text-zinc-500">Выберите версию для просмотра деталей.</p>
                                            </Flex>
                                        )}
                                    </Splitter.Panel>
                                </Splitter>
                            </DetailTabsContent>

                            <DetailTabsContent value="meta" className="">
                                <Card title="Метаданные" className={DETAIL_CARD_CLASS}>
                                    <dl className="space-y-2 text-sm">
                                        <div>
                                            <dt className="eyebrow">ID</dt>
                                            <dd className="font-mono text-xs mt-0.5">{version?.id}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Project Id</dt>
                                            <dd className="font-mono text-xs mt-0.5">{version?.projectId}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">System Name</dt>
                                            <dd className="font-mono text-xs mt-0.5">{version?.systemName || "-"}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Родительский версия</dt>
                                            <dd className="mt-0.5">{version?.parentVersionId ?? "-"}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Версии</dt>
                                            <dd className="mt-0.5">{sortedVersions.length}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Закреплено когда</dt>
                                            <dd className="mt-0.5">{formatDate(version?.lockedAt)}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Обновлён</dt>
                                            <dd className="mt-0.5">{formatDate(version?.updatedAt)}</dd>
                                        </div>
                                        <div>
                                            <dt className="eyebrow">Опубликован</dt>
                                            <dd className="mt-0.5">{formatDate(version?.publishedAt)}</dd>
                                        </div>
                                    </dl>
                                </Card>
                            </DetailTabsContent>
                        </DetailTabs>
                    </div>
                </Flex>

                <VersionEditDialog
                    open={editVersionOpen}
                    projectId={projectId}
                    version={version}
                    onClose={() => setEditVersionOpen(false)}
                    onSaved={async (updated) => {
                        setProject(updated || project);
                        await loadDetails();
                        setEditVersionOpen(false);
                    }}
                />

                <CreateVersionDialog
                    open={createVersionOpen}
                    onClose={() => setCreateVersionOpen(false)}
                    projectId={version.projectId}
                    versions={projectVersions}
                    onCreated={(created) => {
                        setCreateVersionOpen(false);
                        if (created?.id) navigate(`/projects/${projectId}/versions/${created.id}`);
                    }}
                />

                {/* Delete Confirmation Dialog */}
                <Modal
                    open={deleteDialogOpen}
                    onCancel={() => setDeleteDialogOpen(false)}
                    title="Удалить сущность?"
                    closable={false}
                    centered
                    width=""
                    className=""
                    styles={{
                        body: { padding: 0 },
                        content: { overflow: "hidden" },
                    }}
                    onOk={() => { handleVersionDelete(); setDeleteDialogOpen(false); }}
                    footer={(_, { OkBtn, CancelBtn }) => (
                        <>
                            <OkBtn />
                            <CancelBtn />
                        </>
                    )}
                >
                    <div className="text-sm text-zinc-600 dark:text-zinc-400 mb-2">
                        Внимание! Удаление версии может привести к ошибкам, которые её используют.
                    </div>
                    <p>
                        <strong>Project:</strong> {version?.name}
                    </p>
                    <p className="mt-1">
                        <strong>System Name:</strong> {version?.systemName}
                    </p>
                </Modal>
            </div>
        </section>
    );
}